import { zodResolver } from "@hookform/resolvers/zod";
import { Send } from "lucide-react";
import type { ComponentProps } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { useDi } from "~/entities/di";
import {
  InquiryEntity,
  InquiryQuery,
  InquiryServicePorts,
  TK_BY_INQUIRY_SUBJECT,
} from "~/entities/inquiry";
import { Tk, useTranslation2 } from "~/shared/i18n";
import { EMPTY } from "~/shared/str";
import { isObject } from "~/shared/type-guard";
import { cn } from "~/shared/ui";
import { Button } from "~/shared/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/shared/ui/form";
import { Input } from "~/shared/ui/input";
import { PhoneInput } from "~/shared/ui/phone-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/shared/ui/select";
import { Textarea } from "~/shared/ui/textarea";
import { DEFAULT_ZOD_PARSE_PARAMS, errorMapForForms2 } from "~/shared/zod";

const INVALID_CONTACTS = "invalid_contacts";
const CUSTOM_CODE = "customCode";

type InvalidContactsIssue = Omit<z.ZodCustomIssue, "params"> & {
  params: {
    [CUSTOM_CODE]: typeof INVALID_CONTACTS;
  };
};

function isInvalidContactsIssue(value: z.ZodIssueOptionalMessage): value is InvalidContactsIssue {
  return (
    value.code === z.ZodIssueCode.custom &&
    isObject(value.params) &&
    value.params[CUSTOM_CODE] === INVALID_CONTACTS
  );
}

const zSourceSchema = InquiryServicePorts.ProviderIos.Create.zIn;
const zSchema = zSourceSchema.extend({
  contacts: z
    .object({
      email: zSourceSchema.shape.contacts.options[1].shape.email,
      phone: zSourceSchema.shape.contacts.options[0].shape.phone,
    })
    .superRefine((value, ctx) => {
      if (Object.values(value).every((value) => value === EMPTY)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          params: {
            customCode: INVALID_CONTACTS,
          } satisfies InvalidContactsIssue["params"],
        });
      }
    }),
});
type Schema = z.infer<typeof zSchema>;

type Props = ComponentProps<"form">;

function WriteToUsForm({ className, ...props }: Props) {
  const {
    translation: { t },
    postproc: { tc },
  } = useTranslation2();
  const { inquiryService } = useDi();

  const form = useForm({
    resolver: zodResolver(zSchema, {
      ...DEFAULT_ZOD_PARSE_PARAMS,
      errorMap: (issue, ctx) => {
        if (isInvalidContactsIssue(issue)) {
          return {
            message: tc(Tk.PLEASE_PROVIDE_AT_LEAST_ONE_CONTACT_INFORMATION),
          };
        }

        return errorMapForForms2(t)(issue, ctx);
      },
    }),
    defaultValues: {
      fullName: EMPTY,
      subject: InquiryEntity.SubjectSchema.I_D_LIKE_TO_ORDER,
      message: EMPTY,
      contacts: {
        email: EMPTY,
        phone: EMPTY,
      },
    },
  });

  const { mutate: create, isPending: isCreating } = InquiryQuery.useCreate(inquiryService);

  const handleSubmit: Parameters<(typeof form)["handleSubmit"]>[0] = (data) => {
    create(data, {
      onSuccess: () => {
        toast.success(tc(Tk.INQUIRY_CREATED));
        form.reset();
      },
      onError: () => {
        toast.error(tc(Tk.FAILED_TO_CREATE_INQUIRY), {
          description: tc(Tk.AN_UNEXPECTED_ERROR_OCCURRED_PLEASE_TRY_AGAIN_LATER),
        });
      },
    });
  };

  return (
    <Form {...form}>
      <form
        className={cn("flex flex-col gap-4", className)}
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        {...props}
      >
        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc(Tk.FULL_NAME)}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="contacts"
          render={({ field }) => (
            <fieldset className="flex flex-col gap-4 rounded-md border p-6">
              <legend>
                {tc(Tk.CONTACT, {
                  count: 2,
                })}
              </legend>
              {(
                [
                  {
                    name: "email",
                    label: Tk.EMAIL,
                  },
                  {
                    name: "phone",
                    label: Tk.PHONE,
                  },
                ] satisfies {
                  name: keyof Schema["contacts"];
                  label: Tk;
                }[]
              ).map(({ name, label }) => (
                <FormField
                  key={name}
                  control={form.control}
                  name={`contacts.${name}`}
                  render={({ field: subfield }) => (
                    <FormItem>
                      <FormLabel>{tc(label)}</FormLabel>
                      <FormControl>
                        {name === "phone" ? (
                          <PhoneInput
                            value={field.value.phone}
                            onChange={(value) =>
                              field.onChange({
                                ...field.value,
                                phone: value,
                              })
                            }
                            name={subfield.name}
                          />
                        ) : (
                          <Input
                            value={field.value.email}
                            onChange={(event) =>
                              field.onChange({
                                ...field.value,
                                email: event.target.value,
                              })
                            }
                            name={subfield.name}
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
              <FormMessage />
            </fieldset>
          )}
        />
        <FormField
          control={form.control}
          name="subject"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc(Tk.REQUEST_SUBJECT)}</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a verified email to display" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {zSchema.shape.subject.options.map((value) => (
                    <SelectItem key={value} value={value}>
                      {tc(TK_BY_INQUIRY_SUBJECT[value])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc(Tk.MESSAGE)}</FormLabel>
              <FormControl>
                <Textarea {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button size="auto" disabled={isCreating} className="h-15 text-lg">
          {tc(Tk.SEND)}
          <Send />
        </Button>
      </form>
    </Form>
  );
}

export { WriteToUsForm };
